import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Html } from '@react-three/drei';

interface CustomAxesHelperProps {
  size?: number;
  lineWidth?: number;
  showLabels?: boolean;
}

/**
 * Custom Axes Helper with bold lines and HTML labels
 * Z-UP Convention: X=Red (East), Y=Green (North), Z=Blue (Up)
 */
export const CustomAxesHelper: React.FC<CustomAxesHelperProps> = ({
  size = 10,
  lineWidth = 2,
  showLabels = true
}) => {
  // Create bold arrow geometries for each axis
  const arrowGeometries = useMemo(() => {
    const shaftRadius = lineWidth * 0.05;
    const shaftLength = size * 0.75;
    const coneRadius = lineWidth * 0.15;
    const coneLength = size * 0.25;

    return {
      shaft: new THREE.CylinderGeometry(shaftRadius, shaftRadius, shaftLength, 8),
      cone: new THREE.ConeGeometry(coneRadius, coneLength, 8)
    };
  }, [size, lineWidth]);

  // Label positions (slightly beyond arrow tips)
  const labelOffset = size * 1.15;

  return (
    <group>
      {/* X-AXIS (RED) - Horizontal East-West */}
      <group rotation={[0, 0, -Math.PI / 2]}>
        {/* Shaft */}
        <mesh
          position={[0, size * 0.375, 0]}
          geometry={arrowGeometries.shaft}
        >
          <meshBasicMaterial color="#ff0000" />
        </mesh>
        {/* Cone (arrowhead) */}
        <mesh
          position={[0, size * 0.875, 0]}
          geometry={arrowGeometries.cone}
        >
          <meshBasicMaterial color="#ff0000" />
        </mesh>
      </group>
      {showLabels && (
        <Html position={[labelOffset, 0, 0]} center zIndexRange={[0, 10]}>
          <div style={{
            color: '#ff0000',
            fontWeight: 'bold',
            fontSize: '16px',
            fontFamily: 'Arial, sans-serif',
            textShadow: '0 0 3px white, 0 0 5px white',
            pointerEvents: 'none',
            userSelect: 'none'
          }}>
            X
          </div>
        </Html>
      )}

      {/* Y-AXIS (GREEN) - Horizontal North-South */}
      <group rotation={[0, 0, 0]}>
        {/* Shaft */}
        <mesh
          position={[0, size * 0.375, 0]}
          geometry={arrowGeometries.shaft}
        >
          <meshBasicMaterial color="#00ff00" />
        </mesh>
        {/* Cone (arrowhead) */}
        <mesh
          position={[0, size * 0.875, 0]}
          geometry={arrowGeometries.cone}
        >
          <meshBasicMaterial color="#00ff00" />
        </mesh>
      </group>
      {showLabels && (
        <Html position={[0, labelOffset, 0]} center zIndexRange={[0, 10]}>
          <div style={{
            color: '#00ff00',
            fontWeight: 'bold',
            fontSize: '16px',
            fontFamily: 'Arial, sans-serif',
            textShadow: '0 0 3px white, 0 0 5px white',
            pointerEvents: 'none',
            userSelect: 'none'
          }}>
            Y
          </div>
        </Html>
      )}

      {/* Z-AXIS (BLUE) - Vertical Up */}
      <group rotation={[Math.PI / 2, 0, 0]}>
        {/* Shaft */}
        <mesh
          position={[0, size * 0.375, 0]}
          geometry={arrowGeometries.shaft}
        >
          <meshBasicMaterial color="#0000ff" />
        </mesh>
        {/* Cone (arrowhead) */}
        <mesh
          position={[0, size * 0.875, 0]}
          geometry={arrowGeometries.cone}
        >
          <meshBasicMaterial color="#0000ff" />
        </mesh>
      </group>
      {showLabels && (
        <Html position={[0, 0, labelOffset]} center zIndexRange={[0, 10]}>
          <div style={{
            color: '#0000ff',
            fontWeight: 'bold',
            fontSize: '16px',
            fontFamily: 'Arial, sans-serif',
            textShadow: '0 0 3px white, 0 0 5px white',
            pointerEvents: 'none',
            userSelect: 'none'
          }}>
            Z
          </div>
        </Html>
      )}

      {/* Origin sphere */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[lineWidth * 0.08, 16, 16]} />
        <meshBasicMaterial color="#ffff00" />
      </mesh>
    </group>
  );
};
